/**
 * The Traces tab's words and small rules (design "Traces"): the time ranges, the short correlationIds, the labels of
 * the map's nodes and arrows, a request's status pill, the bar colours, the log lines, the open request in the URL and
 * the prompt for "Ask the AI about it". Pure, tested in tracesModel.test.js.
 */
import { kindLabel } from '../../utils/dataModel';
import { formatMs } from './waterfallLayout';
import { searchForHomeMode } from '../../utils/projectTabUrl';

// `ago`: the left end of the chart's axis. The gateway keeps a week of requests (vfs-service TRACES_MAX_AGE_MS).
export const RANGES = [
  { value: '5m', label: 'Last 5 minutes', minutes: 5, ago: '5 minutes ago' },
  { value: '15m', label: 'Last 15 minutes', minutes: 15, ago: '15 minutes ago' },
  { value: '1h', label: 'Last hour', minutes: 60, ago: '1 hour ago' },
  { value: '24h', label: 'Last day', minutes: 1440, ago: '1 day ago' },
  { value: '7d', label: 'Last week', minutes: 10080, ago: '7 days ago' },
];

/** "minute", "8 minutes", "56 minutes", "2 hours": what one bar of the chart counts (the map's `bucketMs`). */
export function bucketText(ms) {
  const minutes = Math.round((Number(ms) || 60000) / 60000);
  if (minutes <= 1) { return 'minute'; }
  if (minutes % 60 === 0) { return minutes === 60 ? 'hour' : `${minutes / 60} hours`; }
  return `${minutes} minutes`;
}
export const DEFAULT_RANGE = '15m';
export const REFRESH_MS = 5000;
export const LIST_LIMIT = 50;

export const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** "7f3a…e21": the first `head` and the last `tail` characters of a long id. */
export function shortId(id, head = 4, tail = 3) {
  const text = String(id || '');
  return text.length > head + tail + 1 ? `${text.slice(0, head)}…${text.slice(-tail)}` : text;
}

/**
 * An arrow's label in words (2026-10-30, "GET / +1 · 5" read as code): its one route and the calls ("POST /messages ·
 * 58 calls"), else how many routes ("2 routes · 6 calls"; "operations" when they are not HTTP routes), just the calls
 * for an unnamed one. `routes`: how many the gateway saw in all (it sends the busiest 10).
 */
export function edgeLabel(edge) {
  const ops = (edge?.operations || []).filter((op) => op?.name);
  const calls = plural(edge?.calls || 0, 'call');
  if (!ops.length) { return calls; }
  const routes = Math.max(edge?.routes || 0, ops.length);
  if (routes === 1) { return `${ops[0].name} · ${calls}`; }
  return `${routes} ${ops.every((op) => /^[A-Z]+ \//.test(op.name)) ? 'routes' : 'operations'} · ${calls}`;
}

/** "3 failed calls" for an arrow with failed calls (its red line), else null. */
export const edgeErrors = (edge) => (edge?.errors > 0 ? plural(edge.errors, 'failed call') : null);

/**
 * An arrow's routes, for its hover card: `[{ key, name, text, bad }]` ("GET /api/items: 5 calls · p95 12 ms · 1
 * failed"), busiest first, then "and 3 more" when the gateway sent only the busiest.
 */
export function edgeRoutes(edge) {
  const ops = edge?.operations || [];
  const rows = ops.map((op, i) => ({
    key: `${op.name || 'call'}-${i}`,
    name: op.name || 'unnamed call',
    text: [plural(op.calls || 0, 'call'), op.p95Ms !== undefined && op.p95Ms !== null ? `p95 ${formatMs(op.p95Ms)}` : null, op.errors ? `${op.errors} failed` : null].filter(Boolean).join(' · '),
    bad: (op.errors || 0) > 0,
  }));
  const more = (edge?.routes || 0) - ops.length;
  return more > 0 ? [...rows, { key: 'more', name: `and ${more} more`, text: '', bad: false }] : rows;
}

/**
 * The map as the page draws it: no arrow without calls, and each node with `received` (the calls of the arrows into
 * it) and `idle` (nothing in the range: dimmed).
 */
export function mapView(map) {
  const edges = (map?.edges || []).filter((edge) => (edge.calls || 0) > 0);
  const received = new Map();
  edges.forEach((edge) => received.set(edge.to, (received.get(edge.to) || 0) + edge.calls));
  const nodes = (map?.nodes || []).map((node) => {
    const into = received.get(node.id) || 0;
    const busy = node.calls > 0 || node.pageLoads > 0 || node.pageRequests > 0 || node.unreached > 0 || into > 0 || edges.some((edge) => edge.from === node.id);
    return { ...node, received: into, idle: !busy };
  });
  return { ...(map || {}), nodes, edges };
}

/**
 * A node's lines under its name: `{ text, bad, where }` ("142 req · p95 40 ms", "55 calls", "3 failed", "58 in").
 * A frontend whose page is open in the browser (`inBrowser`): `where` "in the browser", its page loads (and the
 * requests of its page to its own service) as the text, never an arrow. A service with no span of its own that others
 * call: the calls it got ("6 calls in"), not "0 req". The Visitors node: its requests with no known frontend.
 */
export function nodeSublabel(node) {
  if (!node) { return { text: '', bad: false, where: null }; }
  const calls = node.calls || 0;
  const errors = node.errors || 0;
  const line = (text, bad = false, where = null) => ({ text, bad, where });
  if (node.kind === 'browser') {
    return errors ? line(`${plural(calls, 'request')} · ${errors} failed`, true) : line(`${plural(calls, 'request')} · no frontend`);
  }
  if (node.kind === 'topic') {
    return errors ? line(`${calls} in · ${errors} failed`, true) : line(`${calls} in`);
  }
  if (node.kind === 'service') {
    const where = node.inBrowser ? 'in the browser' : null;
    if (node.unreached && !calls && !(node.pageLoads > node.unreached)) { return line(`${node.unreached} not reached`, true, where); }
    if (node.inBrowser) {
      const failed = (node.pageErrors || 0) + errors;
      const parts = [
        node.pageLoads ? plural(node.pageLoads, 'page load') : null,
        node.pageRequests ? plural(node.pageRequests, 'own request') : null,
        !node.pageLoads && calls ? `${calls} req` : null,
      ].filter(Boolean);
      const text = parts.join(' · ') || 'its page made calls';
      return failed ? line(`${text} · ${failed} failed`, true, where) : line(text, false, where);
    }
    if (!calls && node.received) { return line(`${plural(node.received, 'call')} in`); }
    if (!calls) { return line('no requests'); }
    if (errors) { return line(`${calls} req · ${errors} failed`, true); }
    return line(`${calls} req${node.p95Ms !== undefined && node.p95Ms !== null ? ` · p95 ${formatMs(node.p95Ms)}` : ''}`);
  }
  return errors ? line(`${errors} failed`, true) : line(plural(calls, 'call'));
}

/** A node in words, for a screen reader: "consumer, service, 58 requests, 3 failed". */
export function nodeDescription(node) {
  const kind = { browser: 'requests with no known frontend', service: 'service', topic: 'Kafka topic', datastore: 'datastore', external: 'outside host' }[node?.kind] || 'node';
  let errors = node?.errors ? `, ${node.errors} failed` : ', no errors';
  if (node?.unreached) { errors = `${node?.errors ? `, ${node.errors} failed` : ''}, ${plural(node.unreached, 'request')} never reached it${node.lastReasonWords ? ` (${node.lastReasonWords})` : ''}`; }
  const page = node?.inBrowser
    ? `, its page runs in the browser${node.pageLoads ? `, ${plural(node.pageLoads, 'page load')}` : ''}${node.pageErrors ? `, ${node.pageErrors} failed` : ''}` : '';
  return `${node?.label}, ${kind}${page}, ${plural(node?.calls || 0, node?.kind === 'service' ? 'request' : 'call')}${errors}`;
}

/** The map as a list (a phone): `[{ id, from, to, text, errors, edge }]` ("frontend → bff · 2 routes · 6 calls"), busiest first. */
export function edgeRows(map) {
  const labels = new Map((map?.nodes || []).map((n) => [n.id, n.label]));
  return [...(map?.edges || [])].filter((edge) => (edge.calls || 0) > 0)
    .sort((a, b) => (b.calls || 0) - (a.calls || 0))
    .map((edge) => ({
      id: edge.id,
      from: labels.get(edge.from) || edge.from,
      to: labels.get(edge.to) || edge.to,
      text: `${labels.get(edge.from) || edge.from} → ${labels.get(edge.to) || edge.to} · ${edgeLabel(edge)}`,
      errors: edgeErrors(edge),
      edge,
    }));
}

/** A request's status pill: `{ text, tone: 'bad' | 'ok' }` ("error", else the HTTP status, else "ok"). */
export function statusOf(trace) {
  if (trace?.status === 'error') { return { text: 'error', tone: 'bad' }; }
  if (trace?.httpStatus) { return { text: String(trace.httpStatus), tone: trace.httpStatus >= 400 ? 'bad' : 'ok' }; }
  return { text: 'ok', tone: 'ok' };
}

/** "web › producer › Kafka · messages › consumer". */
export const servicesPath = (trace) => (trace?.services || []).join(' › ');

/**
 * What a failed request comes to (vfs-service's `outcome`): `{ kind: 'unreached' | 'service_error', service, text,
 * target? }`, or null. `unreached`: the preview proxy answered by itself, the request never reached `service`.
 */
export const outcomeOf = (trace) => trace?.outcome || null;

/** Whether the request never reached its service (the preview proxy answered by itself). */
export const isUnreached = (trace) => trace?.outcome?.kind === 'unreached' || trace?.reached === false;

/** The service a request was for (its target), or null. */
export const targetServiceOf = (trace) => trace?.outcome?.target?.service || trace?.target?.service || trace?.outcome?.service || null;

/** A list row's services: `{ text, bad, title }`, "app · not reached" for a request that never reached its service. */
export function rowServices(trace) {
  const service = isUnreached(trace) ? targetServiceOf(trace) : null;
  if (service) { return { text: `${service} · not reached`, bad: true, title: trace?.outcome?.text || undefined }; }
  return { text: servicesPath(trace), bad: false, title: undefined };
}

/** The header's count: "9 spans · 4 services", or "1 span · app not reached" when no service answered. */
export function headerCounts(trace) {
  const spans = plural(trace?.spanCount || (trace?.rows || []).length, 'span');
  const service = isUnreached(trace) ? targetServiceOf(trace) : null;
  if (service && !trace?.serviceCount) { return `${spans} · ${service} not reached`; }
  return `${spans} · ${plural(trace?.serviceCount || 0, 'service')}`;
}

/**
 * The sentence with the service in bold: `[{ text, strong }]` (the first time the service is named), for React to
 * render without HTML.
 */
export function sentenceParts(text, service) {
  const value = String(text || '');
  const at = service ? value.indexOf(service) : -1;
  if (at < 0) { return [{ text: value, strong: false }]; }
  return [
    { text: value.slice(0, at), strong: false },
    { text: service, strong: true },
    { text: value.slice(at + service.length), strong: false },
  ].filter((p) => p.text);
}

/**
 * Which control the target service needs: 'start' (stopped, failed, never built), 'restart' (crashed), or null (it
 * runs, or builds, or the state is unknown). `state`: the service's state now (SandboxService.serviceState), else the
 * trace's.
 */
export function targetControl(state) {
  if (state === 'crashed') { return 'restart'; }
  if (state === 'stopped' || state === 'failed') { return 'start'; }
  return null;
}

/** The arrow's last reason, for a request that never reached the service: "not reached: it crashed, exit code 1". */
export const edgeReason = (edge) => (edge?.lastReasonWords ? `not reached: ${edge.lastReasonWords}` : null);

/** The service's lines around a request that never reached it (`trace.logsAround`), `[]` when none. */
export const logsAroundOf = (trace) => trace?.logsAround?.lines || [];

const pad = (n, size = 2) => String(n).padStart(size, '0');
const hms = (date) => `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;

const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const shortDate = (date) => date.toLocaleDateString('en', { month: 'short', day: 'numeric' });
const withMillis = (date) => `${hms(date)}.${pad(date.getMilliseconds(), 3)}`;

/**
 * When a request started, for people: "today 16:47:12.204" (`millis`) or "today 16:47:12"; another day says its date
 * instead ("Oct 15 16:47:12.204"). Empty without a time.
 */
export function clockTime(ms, now = Date.now(), { millis = false } = {}) {
  if (!ms) { return ''; }
  const date = new Date(ms);
  const time = millis ? withMillis(date) : hms(date);
  return `${sameDay(date, new Date(now)) ? 'today' : shortDate(date)} ${time}`;
}

/** A request's time in the list: "16:47:12" today, "Oct 15 16:47:12" another day. */
export function listTime(ms, now = Date.now()) {
  if (!ms) { return ''; }
  const date = new Date(ms);
  return sameDay(date, new Date(now)) ? hms(date) : `${shortDate(date)} ${hms(date)}`;
}

/** The full date and time, for a title: "Oct 16, 2026, 16:47:12.204". */
export function fullTime(ms) {
  if (!ms) { return ''; }
  const date = new Date(ms);
  return `${date.toLocaleDateString('en', { year: 'numeric', month: 'short', day: 'numeric' })}, ${withMillis(date)}`;
}

/** The ISO time for `<time dateTime>`, or undefined. */
export const isoTime = (ms) => (ms ? new Date(ms).toISOString() : undefined);

/** When a step started, from the request's start: "+12 ms". */
export const offsetLabel = (row) => `+${formatMs(row?.offsetMs || 0)}`;

/** A log line as the panel shows it: "15:42:07.299 ERROR redis set failed: ETIMEDOUT" (no time for the runner's lines). */
export function logLine(line) {
  const date = line?.time ? new Date(line.time) : null;
  const time = date ? `${withMillis(date)} ` : '';
  const level = line?.level === 'error' || line?.level === 'warn' ? `${line.level.toUpperCase()} ` : '';
  return `${time}${level}${line?.text ?? ''}`;
}

/** The service whose logs go with a row: its own (a datastore's row carries its caller's). */
export const logsServiceOf = (row) => row?.service || null;

/** The log lines of that service for this request (`[]` when none). */
export const rowLogs = (trace, row) => (trace?.logs?.[logsServiceOf(row)] || []);

/** "consumer/server.js:41" from `{ path: 'services/consumer/server.js', line: 41 }`. */
export function codeLabel(code) {
  if (!code?.path) { return ''; }
  const path = code.path.replace(/^services\//, '');
  return code.line ? `${path}:${code.line}` : path;
}

/** The Data tab's place for a row's store: `{ store, object? }` (a topic row: the topic), or null. */
export function dataFocusOf(row) {
  if (!row?.store) { return null; }
  const topic = row.nodeKind === 'topic' && typeof row.node === 'string' ? row.node.replace(/^topic:/, '') : null;
  return topic ? { store: row.store, object: topic } : { store: row.store };
}

export const storeName = (store) => kindLabel(store);

/** The first failing step of a request, else null. */
export const failingRow = (trace) => (trace?.rows || []).find((r) => r.status === 'error') || null;

/** The step selected when a request opens: the first that failed, else the root. */
export const defaultRow = (trace) => failingRow(trace) || (trace?.rows || [])[0] || null;

/** "Redis · SET last-message". */
export const rowTitle = (row) => (row ? `${row.label}${row.operation ? ` · ${row.operation}` : ''}` : '');

// Service bars by service name (a stable pick), stores by kind (design colours).
const STORE_COLOURS = { postgres: '#3d6fa8', mysql: '#b06a1f', sqlite: '#6b7280', mongodb: '#2f8f4f', redis: '#c4323a', neo4j: '#1f8a8a', s3: '#c46a00', kafka: '#9b7ad8' };
const SERVICE_COLOURS = ['#5b8def', '#2f6f4f', '#c46a00', '#7a4fb8', '#0e7c86', '#a8577e', '#8a6d1f'];
export const ERROR_COLOUR = '#c4323a';

/** A row's colour: its store's, a topic's purple, an outside host's grey, else its service's. */
export function colourOf(row) {
  if (row?.store && STORE_COLOURS[row.store]) { return STORE_COLOURS[row.store]; }
  if (row?.nodeKind === 'topic') { return STORE_COLOURS.kafka; }
  if (row?.nodeKind === 'external') { return '#6b7280'; }
  const name = String(row?.service || row?.label || '');
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) { hash = (hash * 31 + name.charCodeAt(i)) >>> 0; }
  return SERVICE_COLOURS[hash % SERVICE_COLOURS.length];
}

/** The open request in the URL: `trace=<id>` written in (null drops it); its step (`span`) goes with the request. */
export function searchWithTrace(search, traceId) {
  const params = new URLSearchParams(search || '');
  if (params.get('trace') !== traceId) { params.delete('span'); }
  if (traceId) { params.set('trace', traceId); } else { params.delete('trace'); }
  const text = params.toString();
  return text ? `?${text}` : '';
}

export const traceFromSearch = (search) => new URLSearchParams(search || '').get('trace') || null;

/** Code on a file and line, from where the page is (`?tab=code&path=…&line=…`; the workspace view and its params dropped). */
export function codeSearch(search, code) {
  const params = new URLSearchParams(searchForHomeMode(search, 'files'));
  params.delete('line');
  params.set('path', code.path);
  if (code.line) { params.set('line', String(code.line)); }
  return `?${params.toString()}`;
}

/** The bar chart in words: "Requests per minute over the last 15 minutes: 142 requests, 3 failed in 1 minute." (`bucketMs`: one bar's width.) */
export function chartDescription(perMinute = [], rangeLabel = 'the range', bucketMs = 60000) {
  const unit = bucketText(bucketMs);
  const total = perMinute.reduce((s, b) => s + (b.total || 0), 0);
  const failed = perMinute.reduce((s, b) => s + (b.failed || 0), 0);
  const failedBars = perMinute.filter((b) => b.failed > 0).length;
  const peak = Math.max(0, ...perMinute.map((b) => b.total || 0));
  const per = unit === 'minute' || unit === 'hour' ? unit : `${unit}`;
  const failures = failed ? `${plural(failed, 'failed request')} in ${unit === 'minute' ? plural(failedBars, 'minute') : plural(failedBars, `period of ${per}`, `periods of ${per}`)}` : 'none failed';
  return `Requests per ${unit} over the ${rangeLabel.replace(/^Last /, 'last ')}: ${plural(total, 'request')}, at most ${peak} a ${unit === 'minute' || unit === 'hour' ? unit : `${unit} period`}, ${failures}.`;
}

/**
 * "Ask the AI about it": the request, the step (the one picked, else the one that failed), its error in words and
 * as thrown, where in the code, and that service's log lines for this request.
 */
export function askPrompt(trace, row = null) {
  if (!trace) { return ''; }
  const step = row || failingRow(trace) || defaultRow(trace);
  const failed = trace.status === 'error' || trace.errorCount > 0;
  const lines = [];
  lines.push(failed
    ? 'A request in this project failed. Find out why from the trace below and fix it.'
    : 'Look at this request of the project from its trace below: explain what it does and whether a step is slow or wrong.');
  lines.push('');
  const outcome = outcomeOf(trace);
  if (outcome?.kind === 'unreached') {
    const target = outcome.target || {};
    lines.push(`What happened: ${outcome.text}`);
    const facts = [
      `service ${target.service || outcome.service}`,
      target.state ? `state ${target.state}` : null,
      target.stateNow && target.stateNow !== target.state ? `state now ${target.stateNow}` : null,
      target.reason ? `proxy reason ${target.reason}` : null,
      target.port ? `port ${target.port}` : null,
      target.path ? `path ${target.path}` : null,
      target.exitCode !== undefined && target.exitCode !== null ? `exit code ${target.exitCode}` : null,
      target.restartCount ? `restarted ${target.restartCount} times` : null,
    ].filter(Boolean);
    lines.push(`Target: ${facts.join(', ')}.`);
    if (target.detail) { lines.push(`Proxy error: ${target.detail}`); }
    lines.push('');
  } else if (outcome?.text) {
    lines.push(`What happened: ${outcome.text}`);
    lines.push('');
  }
  lines.push(`Request: ${trace.entry} (correlationId ${trace.correlationId || trace.traceId}${trace.traceId && trace.traceId !== trace.correlationId ? `, traceId ${trace.traceId}` : ''}), ${formatMs(trace.durationMs)}, ${plural(trace.spanCount || (trace.rows || []).length, 'span')} across ${plural(trace.serviceCount || 0, 'service')}${trace.errorCount ? `, ${plural(trace.errorCount, 'error')}` : ''}.`);
  if (step) {
    const by = step.service && step.service !== step.label ? ` (called by ${step.service})` : '';
    lines.push(`${step.status === 'error' ? 'Failing step' : 'Step'}: ${rowTitle(step)}${by}, at +${formatMs(step.offsetMs)}, took ${formatMs(step.durationMs)}.`);
    if (step.error?.words) { lines.push(`Error: ${step.error.words}`); }
    if (step.error?.message && step.error.message !== step.error.words) { lines.push(`Message: ${step.error.message}`); }
    if (step.code?.path) { lines.push(`Code: ${step.code.path}${step.code.line ? ` line ${step.code.line}` : ''}`); }
    const logs = rowLogs(trace, step);
    if (logs.length) {
      lines.push('');
      lines.push(`${logsServiceOf(step)} logs for this request:`);
      logs.forEach((line) => lines.push(logLine(line)));
    }
  }
  const around = logsAroundOf(trace);
  if (around.length) {
    lines.push('');
    lines.push(`${trace.logsAround.service} logs around this request${trace.logsAround.approximate ? ' (its latest lines: no times)' : ' (5 s before and after)'}:`);
    around.forEach((line) => lines.push(logLine(line)));
  }
  return lines.join('\n');
}
